/**
 * School Information Reports
 * Generic report page that renders school-level data in Excel-like tables
 * with pivot columns (category x gender M/F) and filters.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, MenuItem, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Chip, Breadcrumbs, Link, Divider,
} from '@mui/material';
import {
  PlayArrow as GenerateIcon,
  Assessment as ReportIcon,
} from '@mui/icons-material';
import {
  fetchReportTypes, fetchReport, fetchReportDistricts,
  ReportType, ReportData, DistrictOption,
} from '../../services/schoolReportsApi';

const EDUCATION_LEVELS = [
  { value: 'Secondary', label: 'Secondary' },
  { value: 'Primary', label: 'Primary' },
  { value: 'Pre-Primary', label: 'Pre-Primary' },
  { value: 'All', label: 'All Levels' },
];

const SCHOOL_TYPES = [
  { value: 'Government', label: 'Government' },
  { value: 'Private', label: 'Private' },
  { value: 'All', label: 'All (Govt + Private)' },
];

const HEADER_BG = '#1B2A3D';
const HEADER_TEXT = '#FFFFFF';
const SUB_HEADER_BG = '#2C3E50';
const TOTAL_ROW_BG = '#E3F2FD';
const ZEBRA_BG = '#F8FAFC';

const SchoolReports: React.FC = () => {
  // Filters
  const [reportType, setReportType] = useState('enrollment');
  const [year, setYear] = useState(2025);
  const [districtId, setDistrictId] = useState('');
  const [schoolType, setSchoolType] = useState('Government');
  const [educationLevel, setEducationLevel] = useState('Secondary');

  // Data
  const [reportTypes, setReportTypes] = useState<ReportType[]>([]);
  const [districts, setDistricts] = useState<DistrictOption[]>([]);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load report types and districts on mount
  useEffect(() => {
    fetchReportTypes().then(setReportTypes).catch(() => {});
    fetchReportDistricts().then(setDistricts).catch(() => {});
  }, []);

  const handleGenerate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchReport(
        reportType, year,
        districtId || undefined,
        schoolType,
        educationLevel,
      );
      if (result.status === 'success') {
        setReportData(result.data);
      } else {
        setError(result.message || 'Failed to generate report');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  }, [reportType, year, districtId, schoolType, educationLevel]);

  // Group report types by category
  const groupedTypes: Record<string, ReportType[]> = {};
  reportTypes.forEach((rt) => {
    if (!groupedTypes[rt.category]) groupedTypes[rt.category] = [];
    groupedTypes[rt.category].push(rt);
  });

  const isPivot = reportData && !reportData.flat_report;
  const isFlat = reportData && reportData.flat_report;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 1 }}>
          <Link underline="hover" color="inherit" href="/reports">Reports</Link>
          <Typography color="text.primary">School Information Reports</Typography>
        </Breadcrumbs>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          School Information Reports
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Generate school-level reports from live SIS data — enrollment, teachers, infrastructure, and more
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select fullWidth size="small" label="Report Type"
              value={reportType} onChange={(e) => setReportType(e.target.value)}
              SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 450 } } } }}
            >
              {Object.entries(groupedTypes).map(([cat, types]) => [
                <MenuItem key={`cat-${cat}`} disabled sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', mt: 1 }}>
                  {cat}
                </MenuItem>,
                ...types.map((rt) => (
                  <MenuItem key={rt.key} value={rt.key}>{rt.title}</MenuItem>
                )),
              ])}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              fullWidth size="small" label="Year" type="number"
              value={year} onChange={(e) => setYear(Number(e.target.value))}
              inputProps={{ min: 2015, max: 2030 }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              select fullWidth size="small" label="District"
              value={districtId} onChange={(e) => setDistrictId(e.target.value)}
            >
              <MenuItem value="">All Districts</MenuItem>
              {districts.map((d) => (
                <MenuItem key={d.district_id} value={d.district_id}>{d.district_name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              select fullWidth size="small" label="School Type"
              value={schoolType} onChange={(e) => setSchoolType(e.target.value)}
            >
              {SCHOOL_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              select fullWidth size="small" label="Level"
              value={educationLevel} onChange={(e) => setEducationLevel(e.target.value)}
            >
              {EDUCATION_LEVELS.map((l) => (
                <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="contained" fullWidth
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <GenerateIcon />}
              onClick={handleGenerate}
              disabled={loading}
              sx={{ height: 40 }}
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Report Display */}
      {reportData && (
        <Paper sx={{ overflow: 'hidden' }}>
          {/* Report Header */}
          <Box sx={{ p: 2, backgroundColor: '#F5F7FA', borderBottom: '1px solid #E0E0E0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ReportIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {reportData.report_title}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip label={`District: ${reportData.district_name}`} size="small" variant="outlined" />
              <Chip label={`Year: ${reportData.academic_year}`} size="small" variant="outlined" />
              {reportData.summary && (
                <Chip label={`Schools: ${reportData.summary.total_schools}`} size="small" color="primary" />
              )}
              {reportData.summary?.grand_total != null && (
                <Chip label={`Total: ${reportData.summary.grand_total.toLocaleString()}`} size="small" color="success" />
              )}
            </Box>
          </Box>

          <Divider />

          {/* Pivot Table */}
          {isPivot && (
            <TableContainer sx={{ maxHeight: '70vh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  {/* Row 1: Category headers with colspan */}
                  <TableRow>
                    <TableCell rowSpan={2} sx={{ backgroundColor: HEADER_BG, color: HEADER_TEXT, fontWeight: 700, minWidth: 40, position: 'sticky', left: 0, zIndex: 3 }}>
                      S/N
                    </TableCell>
                    <TableCell rowSpan={2} sx={{ backgroundColor: HEADER_BG, color: HEADER_TEXT, fontWeight: 700, minWidth: 180, position: 'sticky', left: 40, zIndex: 3 }}>
                      SCHOOL NAME
                    </TableCell>
                    {reportData.columns.map((col) => (
                      <TableCell
                        key={col.key} colSpan={2} align="center"
                        sx={{ backgroundColor: HEADER_BG, color: HEADER_TEXT, fontWeight: 700, borderLeft: '1px solid rgba(255,255,255,0.15)', fontSize: '0.75rem' }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                    <TableCell colSpan={2} align="center" sx={{ backgroundColor: HEADER_BG, color: HEADER_TEXT, fontWeight: 700, borderLeft: '2px solid rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>
                      TOTAL
                    </TableCell>
                    <TableCell rowSpan={2} align="center" sx={{ backgroundColor: HEADER_BG, color: HEADER_TEXT, fontWeight: 700, borderLeft: '2px solid rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>
                      GRAND TOTAL
                    </TableCell>
                  </TableRow>
                  {/* Row 2: M / F sub-headers */}
                  <TableRow>
                    {reportData.columns.map((col) => (
                      <React.Fragment key={`sub-${col.key}`}>
                        <TableCell align="center" sx={{ backgroundColor: SUB_HEADER_BG, color: HEADER_TEXT, fontWeight: 600, fontSize: '0.7rem', borderLeft: '1px solid rgba(255,255,255,0.1)', py: 0.5 }}>
                          M
                        </TableCell>
                        <TableCell align="center" sx={{ backgroundColor: SUB_HEADER_BG, color: HEADER_TEXT, fontWeight: 600, fontSize: '0.7rem', py: 0.5 }}>
                          F
                        </TableCell>
                      </React.Fragment>
                    ))}
                    <TableCell align="center" sx={{ backgroundColor: SUB_HEADER_BG, color: HEADER_TEXT, fontWeight: 600, fontSize: '0.7rem', borderLeft: '2px solid rgba(255,255,255,0.3)', py: 0.5 }}>
                      M
                    </TableCell>
                    <TableCell align="center" sx={{ backgroundColor: SUB_HEADER_BG, color: HEADER_TEXT, fontWeight: 600, fontSize: '0.7rem', py: 0.5 }}>
                      F
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.schools.map((school, idx) => (
                    <TableRow key={school.sn} sx={{ backgroundColor: idx % 2 === 1 ? ZEBRA_BG : 'white' }}>
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.75rem', position: 'sticky', left: 0, backgroundColor: idx % 2 === 1 ? ZEBRA_BG : 'white', zIndex: 1 }}>
                        {school.sn}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.75rem', whiteSpace: 'nowrap', position: 'sticky', left: 40, backgroundColor: idx % 2 === 1 ? ZEBRA_BG : 'white', zIndex: 1 }}>
                        {school.school_name}
                      </TableCell>
                      {reportData.columns.map((col) => {
                        const vals = school.values[col.key] as Record<string, number> | undefined;
                        const m = vals?.M || 0;
                        const f = vals?.F || 0;
                        return (
                          <React.Fragment key={`${school.sn}-${col.key}`}>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', borderLeft: '1px solid #E8EDF2', color: m ? 'inherit' : '#CCC' }}>
                              {m || ''}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', color: f ? 'inherit' : '#CCC' }}>
                              {f || ''}
                            </TableCell>
                          </React.Fragment>
                        );
                      })}
                      <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, borderLeft: '2px solid #E0E0E0' }}>
                        {school.total_male || ''}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                        {school.total_female || ''}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 700, borderLeft: '2px solid #E0E0E0', color: '#1565C0' }}>
                        {school.grand_total || ''}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Summary Row */}
                  {reportData.summary && (
                    <TableRow sx={{ backgroundColor: TOTAL_ROW_BG }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', position: 'sticky', left: 0, backgroundColor: TOTAL_ROW_BG, zIndex: 1 }} />
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', position: 'sticky', left: 40, backgroundColor: TOTAL_ROW_BG, zIndex: 1 }}>
                        TOTAL ({reportData.summary.total_schools} schools)
                      </TableCell>
                      {reportData.columns.map((col) => {
                        const vals = reportData.summary?.values?.[col.key];
                        return (
                          <React.Fragment key={`sum-${col.key}`}>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', borderLeft: '1px solid #BBDEFB' }}>
                              {vals?.M || ''}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                              {vals?.F || ''}
                            </TableCell>
                          </React.Fragment>
                        );
                      })}
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', borderLeft: '2px solid #90CAF9' }}>
                        {reportData.summary.total_male?.toLocaleString() || ''}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                        {reportData.summary.total_female?.toLocaleString() || ''}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.9rem', borderLeft: '2px solid #90CAF9', color: '#0D47A1' }}>
                        {reportData.summary.grand_total?.toLocaleString() || ''}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Flat Table (Basic Info, WASH) */}
          {isFlat && (
            <TableContainer sx={{ maxHeight: '70vh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: HEADER_BG, color: HEADER_TEXT, fontWeight: 700, minWidth: 40 }}>S/N</TableCell>
                    <TableCell sx={{ backgroundColor: HEADER_BG, color: HEADER_TEXT, fontWeight: 700, minWidth: 180 }}>SCHOOL NAME</TableCell>
                    {reportData.columns.map((col) => (
                      <TableCell key={col.key} align="center" sx={{ backgroundColor: HEADER_BG, color: HEADER_TEXT, fontWeight: 700, fontSize: '0.75rem' }}>
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.schools.map((school, idx) => (
                    <TableRow key={school.sn} sx={{ backgroundColor: idx % 2 === 1 ? ZEBRA_BG : 'white' }}>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{school.sn}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{school.school_name}</TableCell>
                      {reportData.columns.map((col) => (
                        <TableCell key={col.key} align={col.type === 'text' ? 'left' : 'right'} sx={{ fontSize: '0.75rem' }}>
                          {String(school.values[col.key] ?? '')}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Empty state */}
          {reportData.schools.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No data found for the selected filters.</Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Initial state */}
      {!reportData && !loading && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <ReportIcon sx={{ fontSize: 48, color: '#CCC', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Select a report type and click Generate Report
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Reports are generated from live SIS data with school-level detail
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default SchoolReports;
